import { calcSpacing } from 'src/components/foundations';
import { textStyleCss } from 'src/theme/atlas';
import styled, { css } from 'styled-components';

const columnStackSm = css`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(0.8)}px;
`;

const bodyTextMutedN600 = css`
  margin: 0;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
`;

const borderedConfigPanel = css`
  border: 1px solid
    ${(props) => props.theme.atlas.semantic.color.ui.divider.default};
  border-radius: ${(props) => props.theme.atlas.semantic.radius.lg};
  padding: ${calcSpacing(1.6)}px;
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1.6)}px;
  width: 100%;
  box-sizing: border-box;
`;

export const ConfigContent = styled.div`
  padding: ${calcSpacing(2)}px;
  border: 1px solid
    ${(props) => props.theme.atlas.semantic.color.ui.divider.default};
  border-radius: ${(props) => props.theme.atlas.semantic.radius.lg};
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1.6)}px;
`;

export const DuplicateManagementCategorySurface = styled.div`
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  border-radius: ${(props) => props.theme.atlas.semantic.radius.lg};
  display: flex;
  flex-direction: column;
  box-shadow: none;
`;

export const CategoryConfigurationExpanded = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  gap: ${calcSpacing(1.6)}px;
`;

export const CategoryPanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1)}px;
  width: 100%;
`;

export const CategoryPanelTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${calcSpacing(1)}px;
`;

export const CategoryPanelTitle = styled.h2`
  margin: 0;
  ${(props) => textStyleCss(props.theme.atlas.typography.semantic.headingH3)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const CategoryPanelSubtext = styled.p`
  ${bodyTextMutedN600}
  max-width: 100%;
`;

export const InfoTooltipTrigger = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: top;
`;

export const InfoTooltipAnchor = styled.div`
  display: inline-flex;
  align-items: center;
`;

export const InfoTooltipMessage = styled.div`
  display: inline-block;
  width: auto;
  min-width: 0;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  text-align: center;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const InfoTooltipButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  color: ${(props) => props.theme.atlas.semantic.color.type.muted};
  cursor: pointer;
  border-radius: 50%;
  &:hover {
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.action.primary.default};
    outline-offset: 2px;
  }
`;

export const FormField = styled.div`
  ${columnStackSm}
`;

export const FormLabel = styled.label`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  display: flex;
  align-items: center;
  gap: ${calcSpacing(0.4)}px;
`;

export const RequiredText = styled.span`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
  font-weight: 600;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const CategoryToggleStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(2)}px;
  width: 100%;
`;

export const SwitchSection = styled.div`
  ${columnStackSm}
`;

export const SwitchHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${calcSpacing(0.8)}px;
  min-height: 18px;
`;

export const SwitchLabel = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
`;

export const SwitchHelpText = styled.p`
  ${bodyTextMutedN600}
`;

export const SectionTitle = styled.h3`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  font-weight: 600;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
`;

export const IntakeSectionTitle = styled(SectionTitle)`
  margin-bottom: 0;
`;

export const SectionDescription = styled.p`
  ${bodyTextMutedN600}
`;

export const IntakeChannelsCard = styled.div`
  ${borderedConfigPanel}
  gap: ${calcSpacing(1)}px;
`;

export const IntakeChannelsSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

export const IntakeChannelRows = styled.div`
  ${columnStackSm}
  width: 100%;
`;

export const IntakeChannelRow = styled.div<{ $inset?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${calcSpacing(1.2)}px;
  min-height: 24px;
  width: 100%;
  box-sizing: border-box;
  padding-left: ${(props) => (props.$inset ? `${calcSpacing(2.4)}px` : '0')};
`;

export const IntakeChannelRowLabel = styled.span<{ $disabled?: boolean }>`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  color: ${(props) =>
    props.$disabled
      ? props.theme.atlas.semantic.color.type.disabled
      : props.theme.atlas.semantic.color.type.muted};
  text-align: left;
`;

export const CountryCard = styled.div`
  ${borderedConfigPanel}
`;

export const FormSection = styled.div`
  ${columnStackSm}
`;

export const LanguagesLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1.6)}px;
  width: 100%;
`;

export const CountryLabel = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  font-weight: 600;
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
`;

export const LanguagesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: ${calcSpacing(0.8)}px;
  position: relative;
`;

export const AddLanguageButtonWrapper = styled.div`
  position: relative;

  & > button {
    cursor: pointer;
  }

  & i {
    pointer-events: none;
  }
`;

export const AddLanguageDropdownWrapper = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  z-index: 1000;
  margin-top: ${calcSpacing(0.5)}px;
`;

export const LanguageTags = styled.div`
  display: flex;
  gap: ${calcSpacing(0.8)}px;
  flex-wrap: wrap;
`;

export const SuggestedReadingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${calcSpacing(1.6)}px;
  margin-top: ${calcSpacing(2.4)}px;
`;

export const SuggestedReadingTitle = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.body)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
  margin-bottom: ${calcSpacing(0.8)}px;
`;

export const SuggestedReadingLinks = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SuggestedReadingLink = styled.div`
  display: flex;
  align-items: center;
  gap: ${calcSpacing(0.4)}px;
  padding: ${calcSpacing(0.4)}px 0;
  cursor: pointer;
  border-radius: 3px;

  span {
    ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }

  i {
    width: 16px;
    height: 16px;
    padding-top: 0;
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }

  &:hover {
    background-color: ${(props) =>
      props.theme.atlas.semantic.color.surface.variant};
  }
`;

export const SupportText = styled.p`
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.caption)};
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  margin: 0;
`;

export const ReadOnlyCategoryName = styled.div`
  min-height: 114px;
  width: 100%;
  padding: 8px 16px;
  margin: 0;
  border: 1px solid
    ${(props) => props.theme.atlas.semantic.color.ui.divider.default};
  border-radius: 3px;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.subtle};
  box-sizing: border-box;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
  display: flex;
  align-items: flex-start;
`;

export const CategoryNameDropdownWrapper = styled.div`
  width: 100%;
`;

export const CategoryDescriptionWrapper = styled.div`
  textarea {
    margin: 0;
    overflow: auto;
  }

  .quill {
    max-height: 114px;
    overflow: auto;
    border: 1px solid
      ${(props) => props.theme.atlas.semantic.color.ui.divider.default};
    border-radius: 3px;
    padding: 8px 16px;
    margin: 0;
    background-color: ${(props) =>
      props.theme.atlas.semantic.color.surface.subtle};
    ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }

  .ql-container {
    border: none;
    font-family: inherit;
  }

  .ql-editor {
    padding: 0;
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }

  .ql-editor p,
  .ql-editor li,
  .ql-editor h1,
  .ql-editor h2,
  .ql-editor h3,
  .ql-editor h4,
  .ql-editor h5,
  .ql-editor h6,
  .ql-editor blockquote,
  .ql-editor strong,
  .ql-editor em {
    color: inherit;
  }
`;
